/**
 * AccountDashboard - Tabbed Account Dashboard
 * MAESTRO Platform v1.6.0
 *
 * Full-featured customer account page with tabs:
 * - Dashboard: Overview, stats, recent orders
 * - Orders: Order history with refund functionality
 * - Addresses: Address book management
 * - Payment: Payment methods (placeholder)
 * - Wishlist: Saved items (placeholder)
 * - Collection: Collection tracker (placeholder)
 * - Rewards: Loyalty rewards (placeholder)
 * - Returns: Return requests (placeholder)
 *
 * Integrates with:
 * - ordersAPI for order history
 * - refundsAPI for refund requests
 * - shippingAPI for address management
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  User, Package, MapPin, CreditCard, Heart, Gift, RotateCcw, Bell,
  HelpCircle, BookOpen, Eye, TrendingUp, ChevronRight, Search, Download,
  Truck, CheckCircle, Clock, AlertCircle, Plus, Edit2, Trash2, Star, X,
  Settings, LogOut, Home, Loader2, ArrowLeft, Send, RefreshCw, AlertTriangle, Mail
} from 'lucide-react';
import { ordersAPI, refundsAPI, shippingAPI, authAPI, emailPreferencesAPI } from '../services/api';
import { useRefundEligibility } from '../hooks/useRefundEligibility';
import EmailPreferencesPage from './email/EmailPreferencesPage';
import { SubscriptionStatus } from './subscription';
import AvatarManager from './avatar/AvatarManager';
// IDENTITY:P3.B — IdentityPreview for account dashboard
import IdentityPreview from './identity/IdentityPreview';
import { useIdentityContext } from '../contexts/IdentityContext';
import { FEATURES } from '../features';

// =============================================================================
// REFUND CONSTANTS & COMPONENTS (from MyOrders.jsx)
// =============================================================================
const REFUND_REASONS = [
  { value: 'DEFECTIVE', label: 'Defective/Damaged Item' },
  { value: 'WRONG_ITEM', label: 'Wrong Item Received' },
  { value: 'NOT_AS_DESCRIBED', label: 'Not as Described' },
  { value: 'CHANGED_MIND', label: 'Changed My Mind' },
  { value: 'OTHER', label: 'Other' },
];

// Order status badge component
function OrderStatusBadge({ status }) {
  const statusConfig = {
    pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, label: 'Pending' },
    confirmed: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle, label: 'Confirmed' },
    processing: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Package, label: 'Processing' },
    shipped: { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: Truck, label: 'Shipped' },
    delivered: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, label: 'Delivered' },
    cancelled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: X, label: 'Cancelled' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// Refund eligibility badge — the SERVER decides.
//
// ⛔ THIS USED TO COMPUTE THE ANSWER ITSELF, with a literal that also lived in three other
// components and, separately, in BCWRefundService.validate_refund_eligibility. Two copies of a
// money rule cannot be kept in agreement by hoping: a customer could be told "Returns Accepted"
// on an item the server then refused.
//
// ⚠ UNKNOWN RENDERS NOTHING — absent rather than optimistic while the verdict is in flight.
function RefundEligibilityBadge({ eligibility }) {
  if (!eligibility) return null;

  if (eligibility.is_eligible) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30">
        <RefreshCw className="w-3 h-3" />
        Returns Accepted
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">
      <AlertTriangle className="w-3 h-3" />
      Final Sale
    </span>
  );
}

// Refund request modal
function RefundRequestModal({ item, order, eligibility, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      setError('Please select a reason');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await refundsAPI.createRequest(order.id, item.id, reason, details || null);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to submit refund request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-white">Request Refund</h3>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="flex gap-3 p-3 bg-zinc-800/50 rounded-lg">
            <img
              src={item.image || item.image_url || 'https://placehold.co/80x100/27272a/f59e0b?text=Item'}
              alt={item.name}
              className="w-16 h-20 object-cover rounded"
            />
            <div className="flex-1">
              <p className="font-medium text-white">{item.name}</p>
              <p className="text-sm text-zinc-400">Qty: {item.quantity}</p>
              <p className="text-sm text-orange-500 font-semibold">${item.price?.toFixed(2)}</p>
            </div>
          </div>

          {/* ⛔ WHAT THE CUSTOMER WILL ACTUALLY RECEIVE, BEFORE THEY CONFIRM.
              This block used to be static prose -- "within 30 days ... A 15% restocking fee
              applies" -- which was a FOURTH place those two numbers were written down, and it
              never showed the net. A fee that only appears on the settlement is a fee nobody
              agreed to.

              Every figure below comes from the server, computed with the same Decimal
              arithmetic create_refund_request uses. Recomputing them here in JavaScript floats
              would be a second copy of the money math, and the number shown would eventually
              stop matching the number charged. */}
          <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-zinc-400 flex-1">
                <p className="text-orange-400 font-medium mb-1">Refund Policy</p>
                {eligibility?.policy_summary && <p className="mb-2">{eligibility.policy_summary}</p>}
                {eligibility?.net_refund != null && (
                  <div className="space-y-1 pt-2 border-t border-orange-500/20">
                    <div className="flex justify-between">
                      <span>Item total</span>
                      <span className="text-zinc-300">${eligibility.item_total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Restocking fee ({eligibility.restocking_fee_percent}%)</span>
                      <span className="text-orange-400">-${eligibility.restocking_fee?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-1 border-t border-orange-500/20">
                      <span className="text-zinc-300">You will receive</span>
                      <span className="text-green-400">${eligibility.net_refund?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                {eligibility?.return_window_days != null && (
                  <p className="mt-2">
                    Return window: {eligibility.return_window_days} days from delivery.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Reason for Refund <span className="text-red-400">*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
              required
            >
              <option value="">Select a reason...</option>
              {REFUND_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Additional Details (Optional)
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Please provide any additional information..."
              rows={3}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 resize-none"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-2.5 bg-zinc-700 text-white rounded-lg font-medium hover:bg-zinc-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !reason}
              className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// EMAIL HISTORY SECTION (Phase 6 — MDM-EMAIL-P6-001)
// =============================================================================
const EMAIL_TYPE_CONFIG = {
  digest: { display_name: 'Collection Digest', badge_color: 'green' },
  convention_alert: { display_name: 'Convention Alert', badge_color: 'purple' },
  gap_alert: { display_name: 'Gap Alert', badge_color: 'amber' },
  campaign: { display_name: 'Campaign', badge_color: 'blue' },
  transactional: { display_name: 'Order Confirmation', badge_color: 'zinc' },
};

const EMAIL_STATUS_CONFIG = {
  delivered: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Delivered' },
  opened: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Opened' },
  clicked: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Clicked' },
  bounced: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Bounced' },
  queued: { color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', label: 'Queued' },
};

function EmailHistorySection({ activeTab }) {
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryPage, setEmailHistoryPage] = useState(1);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [emailHistoryTotal, setEmailHistoryTotal] = useState(0);
  const perPage = 10;

  useEffect(() => {
    /* c8 ignore next */
    if (activeTab !== 'email') return;
    let cancelled = false;
    setEmailHistoryLoading(true);
    emailPreferencesAPI.getEmailHistory(emailHistoryPage, perPage)
      .then((res) => {
        /* c8 ignore next 3 */
        if (cancelled) return;
        setEmailHistory(res.items || []);
        setEmailHistoryTotal(res.total || 0);
      })
      /* c8 ignore next 4 */
      .catch(() => {
        if (cancelled) return;
        setEmailHistory([]);
        setEmailHistoryTotal(0);
      })
      .finally(() => {
        if (!cancelled) setEmailHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, emailHistoryPage]);

  const totalPages = Math.max(1, Math.ceil(emailHistoryTotal / perPage));

  /* c8 ignore start */
  const getTypeConfig = (emailType) => {
    return EMAIL_TYPE_CONFIG[emailType] || {
      display_name: emailType ? emailType.charAt(0).toUpperCase() + emailType.slice(1).replace(/_/g, ' ') : 'Unknown',
      badge_color: 'zinc',
    };
  };
  /* c8 ignore stop */

  const getStatusConfig = (status) => {
    return EMAIL_STATUS_CONFIG[status] || EMAIL_STATUS_CONFIG.queued;
  };

  const badgeColorClasses = {
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    zinc: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  };

  return (
    <div className="mt-6 bg-zinc-900 rounded-lg border border-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-800">
        <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
          <Mail className="w-4 h-4 text-orange-500" />
          Email History
        </h3>
      </div>

      {emailHistoryLoading ? (
        <div className="p-4 space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-10 bg-zinc-800 rounded animate-pulse" />
          ))}
        </div>
      ) : emailHistory.length === 0 ? (
        <div className="p-8 text-center">
          <Mail className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">No email history yet.</p>
          <p className="text-xs text-zinc-500 mt-1">Your sent emails will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800">
                <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Subject</th>
                <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Sent</th>
                <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {emailHistory.map((record) => {
                const typeConfig = getTypeConfig(record.email_type);
                const statusConfig = getStatusConfig(record.status);
                const sentDate = record.sent_at || record.created_at;
                return (
                  <tr key={record.id} className="hover:bg-zinc-800/50 transition-colors">
                    <td className="px-4 py-2.5">
                      <span
                        /* c8 ignore next */
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${badgeColorClasses[typeConfig.badge_color] || badgeColorClasses.zinc}`}
                        aria-label={`Type: ${typeConfig.display_name}`}
                      >
                        {typeConfig.display_name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-200">{typeConfig.display_name}</td>
                    <td className="px-4 py-2.5 text-zinc-400">
                      {sentDate ? new Date(sentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusConfig.color}`}
                        aria-label={`Status: ${statusConfig.label}`}
                      >
                        {statusConfig.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {emailHistoryTotal > perPage && (
        <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-between" aria-label="Email history pagination">
          <button
            /* c8 ignore next */
            onClick={() => setEmailHistoryPage((p) => Math.max(1, p - 1))}
            disabled={emailHistoryPage <= 1}
            className="px-3 py-1.5 text-xs font-medium rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-xs text-zinc-400">
            Page <span className="text-orange-500 font-medium">{emailHistoryPage}</span> of {totalPages}
          </span>
          <button
            onClick={() => setEmailHistoryPage((p) => Math.min(totalPages, p + 1))}
            disabled={emailHistoryPage >= totalPages}
            className="px-3 py-1.5 text-xs font-medium rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function AccountDashboard({ user, onClose, onLogout }) {
  // IDENTITY:P3.B — Retrieve identity for IdentityPreview
  let identity = null;
  try {
    const ctx = useIdentityContext();
    identity = ctx.identity;
  } catch {
    // May render outside IdentityProvider — fall back gracefully
  }

  const [activeTab, setActiveTab] = useState('dashboard');

  // Data states
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState({ orders: true, addresses: true });
  const [error, setError] = useState({ orders: null, addresses: null });

  // UI states
  const [selectedOrder, setSelectedOrder] = useState(null);
  // Hoisted to the component body because renderOrderDetail is a helper, not a component --
  // a hook called inside it would break the rules-of-hooks ordering. Keyed on the selected
  // order, so one request covers every row of the detail view.
  const orderEligibility = useRefundEligibility(selectedOrder?.id);
  const [refundItem, setRefundItem] = useState(null);
  const [notification, setNotification] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Tab configuration
  // ⛔ FOUR TABS ADVERTISED FEATURES THAT DO NOT EXIST. Payment, Wishlist, Collection and
  // Rewards each rendered a placeholder ("Manage your payment methods", "Save items for later",
  // …) — a beta user clicking them learns the product is emptier than it looked, which is worse
  // than not offering them. Hidden for beta and un-hidden individually as each becomes real.
  //
  // Returns is NOT in this list, deliberately: #565 replaces its placeholder with the
  // customer's own refund requests and a cancel action. On THIS branch it is still a
  // placeholder, so the two land together or Returns is briefly a dead tab like the rest.
  //
  // Rewards is tied to GAMIFICATION_ENABLED rather than listed flat, so it comes back with the
  // feature it belongs to instead of needing a second decision that someone has to remember.
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'orders', label: 'Orders', icon: Package, badge: orders.filter(o => o.status === 'shipped' || o.status === 'processing').length || null },
    { id: 'addresses', label: 'Addresses', icon: MapPin },
    ...(FEATURES.GAMIFICATION_ENABLED ? [{ id: 'rewards', label: 'Rewards', icon: Gift }] : []),
    { id: 'returns', label: 'Returns', icon: RotateCcw },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'avatar', label: 'Avatar', icon: User },
  ];

  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, orders: true }));
      setError(prev => ({ ...prev, orders: null }));
      const response = await ordersAPI.getMyOrders();
      setOrders(response.orders || response);
    } catch (err) {
      setError(prev => ({ ...prev, orders: err.message || 'Failed to load orders' }));
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  }, []);

  const fetchAddresses = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, addresses: true }));
      setError(prev => ({ ...prev, addresses: null }));
      const response = await shippingAPI.getAddresses();
      setAddresses(response.addresses || response);
    } catch (err) {
      setError(prev => ({ ...prev, addresses: err.message || 'Failed to load addresses' }));
    } finally {
      setLoading(prev => ({ ...prev, addresses: false }));
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchAddresses();
  }, [fetchOrders, fetchAddresses]);

  // =============================================================================
  // NOTIFICATIONS
  // =============================================================================
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // =============================================================================
  // ORDER HELPERS
  // =============================================================================
  const canRequestRefund = (order, item, eligibility) => {
    // The server answers both halves — refundable product type AND an open return window. The
    // hardcoded `daysSinceDelivery <= 30` that stood here was the only window check anywhere,
    // so a direct API call bypassed it; it is enforced in create_refund_request now and read
    // from the policy row here, so the number is never written down twice.
    if (!eligibility) return false;
    if (order.status !== 'delivered') return false;
    return eligibility.is_eligible && eligibility.within_return_window !== false;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-emerald-400 bg-emerald-400/10';
      case 'shipped': return 'text-blue-400 bg-blue-400/10';
      case 'processing': return 'text-amber-400 bg-amber-400/10';
      case 'pending': return 'text-orange-400 bg-orange-400/10';
      default: return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return CheckCircle;
      case 'shipped': return Truck;
      case 'processing': return Clock;
      default: return AlertCircle;
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        order.order_number?.toLowerCase().includes(query) ||
        order.items?.some(item => item.name?.toLowerCase().includes(query))
      );
    }
    return true;
  });

  // =============================================================================
  // ADDRESS HELPERS
  // =============================================================================
  const handleDeleteAddress = async (addressId) => {
    if (!confirm('Are you sure you want to delete this address?')) return;

    try {
      await shippingAPI.deleteAddress(addressId);
      setAddresses(prev => prev.filter(a => a.id !== addressId));
      showNotification('Address deleted');
    } catch (err) {
      showNotification(err.message || 'Failed to delete address', 'error');
    }
  };

  // =============================================================================
  // STATS CALCULATIONS
  // =============================================================================
  const stats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'processing' || o.status === 'shipped').length,
    totalSpent: orders.reduce((sum, o) => sum + (o.total || 0), 0),
  };

  // =============================================================================
  // RENDER: DASHBOARD TAB
  // =============================================================================
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-orange-600/20 to-amber-600/10 border border-orange-500/30 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">Welcome back, {user?.name?.split(' ')[0] || 'Guest'}!</h1>
            <p className="text-zinc-400 mt-1 text-sm">Here's what's happening with your account</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-full text-sm font-medium">
            <Star className="w-4 h-4 fill-amber-400" />
            Member
          </div>
        </div>
      </div>

      {/* MDM-UNIFIED-001: Subscription Status */}
      <SubscriptionStatus />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Total Orders', value: stats.totalOrders.toString(), subValue: stats.pendingOrders > 0 ? `${stats.pendingOrders} pending` : null, icon: Package, color: 'orange', onClick: () => setActiveTab('orders') },
          { label: 'Total Spent', value: `$${stats.totalSpent.toFixed(2)}`, subValue: null, icon: CreditCard, color: 'green', onClick: () => setActiveTab('orders') },
          { label: 'Addresses', value: addresses.length.toString(), subValue: null, icon: MapPin, color: 'blue', onClick: () => setActiveTab('addresses') },
          { label: 'Member Since', value: user?.created_at ? new Date(user.created_at).getFullYear().toString() : new Date().getFullYear().toString(), subValue: null, icon: User, color: 'purple', onClick: null },
        ].map((stat, i) => (
          <div
            key={i}
            onClick={stat.onClick}
            className={`bg-zinc-900 border border-zinc-800 rounded-xl p-3 sm:p-4 transition-colors ${stat.onClick ? 'hover:border-orange-500/50 cursor-pointer' : ''} group`}
          >
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                stat.color === 'orange' ? 'bg-orange-500/20' :
                stat.color === 'green' ? 'bg-green-500/20' :
                stat.color === 'blue' ? 'bg-blue-500/20' :
                'bg-purple-500/20'
              }`}>
                <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${
                  stat.color === 'orange' ? 'text-orange-500' :
                  stat.color === 'green' ? 'text-green-500' :
                  stat.color === 'blue' ? 'text-blue-500' :
                  'text-purple-500'
                }`} />
              </div>
              {stat.onClick && <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-orange-500 transition-colors" />}
            </div>
            <p className="text-xl sm:text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-xs sm:text-sm text-zinc-500">{stat.label}</p>
            {stat.subValue && (
              <p className="text-xs mt-1 text-orange-400">{stat.subValue}</p>
            )}
          </div>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-base sm:text-lg font-semibold text-white">Recent Orders</h2>
          <button onClick={() => setActiveTab('orders')} className="text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1 transition-colors">
            View All <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {loading.orders ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : error.orders ? (
          <div className="p-4 text-center text-red-400">{error.orders}</div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No orders yet</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {orders.slice(0, 3).map((order) => {
              const StatusIcon = getStatusIcon(order.status);
              return (
                <div
                  key={order.id}
                  onClick={() => { setSelectedOrder(order); setActiveTab('orders'); }}
                  className="p-3 sm:p-4 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-white text-sm sm:text-base truncate">#{order.order_number}</p>
                        <p className="text-xs sm:text-sm text-zinc-500">
                          {order.items?.length || 0} items • {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium capitalize flex items-center gap-1 ${getStatusColor(order.status)}`}>
                        <StatusIcon className="w-3 h-3" />
                        <span className="hidden sm:inline">{order.status}</span>
                      </span>
                      <span className="text-white font-medium text-sm sm:text-base">${order.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-400 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            onClick={() => setActiveTab('orders')}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-zinc-300 transition-colors"
          >
            <Package className="w-4 h-4" />
            View Orders
          </button>
          <button
            onClick={() => setActiveTab('addresses')}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-zinc-300 transition-colors"
          >
            <MapPin className="w-4 h-4" />
            Addresses
          </button>
          <button
            onClick={() => setActiveTab('returns')}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-zinc-300 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Returns
          </button>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-red-500/20 rounded-lg text-sm text-zinc-300 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );

  // =============================================================================
  // RENDER: ORDER DETAIL
  // =============================================================================
  const renderOrderDetail = () => {
    const order = selectedOrder;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedOrder(null)}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <div>
            <h3 className="text-lg font-semibold text-white">Order #{order.order_number}</h3>
            <p className="text-sm text-zinc-500">
              {new Date(order.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="ml-auto">
            <OrderStatusBadge status={order.status} />
          </div>
        </div>

        {/* Tracking info */}
        {order.status === 'shipped' && order.tracking_number && (
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-400" />
              <span className="text-zinc-400">Tracking:</span>
              <span className="text-blue-400 font-mono">{order.tracking_number}</span>
            </div>
          </div>
        )}

        {/* Order items */}
        <div className="bg-zinc-800/50 rounded-xl border border-zinc-700 overflow-hidden">
          <div className="p-4 border-b border-zinc-700">
            <h4 className="font-medium text-white">Items</h4>
          </div>
          <div className="divide-y divide-zinc-700">
            {order.items?.map((item) => (
              <div key={item.id} className="p-4 flex items-center gap-4">
                <img
                  src={item.image || item.image_url || 'https://placehold.co/60x80/27272a/f59e0b?text=Item'}
                  alt={item.name}
                  className="w-16 h-20 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{item.name}</p>
                  <p className="text-sm text-zinc-400">Qty: {item.quantity}</p>
                  <RefundEligibilityBadge eligibility={orderEligibility.forItem(item.id)} />
                </div>
                <div className="text-right">
                  <p className="font-semibold text-white">${(item.price * item.quantity).toFixed(2)}</p>
                  {canRequestRefund(order, item, orderEligibility.forItem(item.id)) && (
                    <button
                      onClick={() => setRefundItem({ item, order })}
                      className="mt-2 text-xs text-orange-500 hover:text-orange-400 font-medium"
                    >
                      Request Refund
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order summary */}
        <div className="bg-zinc-800/50 rounded-xl border border-zinc-700 p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-400">Subtotal</span>
            <span className="text-white">${order.subtotal?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-400">Shipping</span>
            <span className="text-white">{order.shipping_cost > 0 ? `$${order.shipping_cost?.toFixed(2)}` : 'FREE'}</span>
          </div>
          <div className="flex justify-between text-sm pt-2 border-t border-zinc-700">
            <span className="font-medium text-white">Total</span>
            <span className="font-semibold text-orange-500">${order.total?.toFixed(2)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5">
            <Download className="w-4 h-4" /> Download Invoice
          </button>
        </div>
      </div>
    );
  };

  // =============================================================================
  // RENDER: ORDERS TAB
  // =============================================================================
  const renderOrders = () => {
    if (selectedOrder) return renderOrderDetail();

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-lg sm:text-xl font-bold text-white">Order History</h1>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:flex-initial">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-auto pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>
        </div>

        {loading.orders ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : error.orders ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-400 mb-4">{error.orders}</p>
            <button
              onClick={fetchOrders}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Try Again
            </button>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400">{orders.length === 0 ? 'No orders yet' : 'No matching orders'}</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {filteredOrders.map((order) => {
              const StatusIcon = getStatusIcon(order.status);
              return (
                <div key={order.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <div
                    onClick={() => setSelectedOrder(order)}
                    className="p-3 sm:p-4 border-b border-zinc-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                  >
                    <div>
                      <p className="font-semibold text-white">#{order.order_number}</p>
                      <p className="text-sm text-zinc-500">Placed {new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize flex items-center gap-1.5 ${getStatusColor(order.status)}`}>
                        <StatusIcon className="w-3 h-3" />
                        {order.status}
                      </span>
                      <span className="text-lg font-semibold text-white">${order.total?.toFixed(2)}</span>
                    </div>
                  </div>

                  {order.status === 'shipped' && order.tracking_number && (
                    <div className="px-3 sm:px-4 py-2 sm:py-3 bg-zinc-800/50 border-b border-zinc-800">
                      <div className="flex items-center gap-2 text-sm">
                        <Truck className="w-4 h-4 text-blue-400" />
                        <span className="text-zinc-400">Tracking:</span>
                        <span className="text-orange-400 font-mono text-xs sm:text-sm">{order.tracking_number}</span>
                      </div>
                    </div>
                  )}

                  <div className="p-3 sm:p-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="px-3 sm:px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      View Details
                    </button>
                    <button className="px-3 sm:px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5">
                      <Download className="w-4 h-4" /> Invoice
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // =============================================================================
  // RENDER: ADDRESSES TAB
  // =============================================================================
  const renderAddresses = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg sm:text-xl font-bold text-white">Address Book</h1>
        <button className="px-3 sm:px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add</span> Address
        </button>
      </div>

      {loading.addresses ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : error.addresses ? (
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400">{error.addresses}</p>
        </div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <MapPin className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">No addresses saved</p>
          <p className="text-sm text-zinc-500 mt-2">Add an address for faster checkout</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {addresses.map((addr) => (
            <div key={addr.id} className={`bg-zinc-900 border rounded-xl p-4 sm:p-5 relative ${addr.is_default ? 'border-orange-500' : 'border-zinc-800'}`}>
              {addr.is_default && (
                <span className="absolute top-3 right-3 bg-orange-500/20 text-orange-400 text-xs font-medium px-2 py-1 rounded-full">
                  Default
                </span>
              )}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-zinc-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white">{addr.nickname || addr.address_type || 'Address'}</p>
                  <p className="text-zinc-400 mt-1 text-sm">{addr.contact_name || addr.name}</p>
                  <p className="text-zinc-400 text-sm">{addr.street_address || addr.street}</p>
                  <p className="text-zinc-400 text-sm">{addr.city}, {addr.state} {addr.postal_code || addr.zip}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-800">
                <button className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1.5">
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
                {!addr.is_default && (
                  <button
                    onClick={() => handleDeleteAddress(addr.id)}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-red-500/20 text-zinc-400 hover:text-red-400 text-sm rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // =============================================================================
  // RENDER: PLACEHOLDER TABS
  // =============================================================================
  const renderPlaceholder = (title, description) => (
    <div className="flex items-center justify-center h-64 bg-zinc-900 border border-zinc-800 rounded-xl">
      <div className="text-center">
        <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
          {tabs.find(t => t.id === activeTab)?.icon &&
            React.createElement(tabs.find(t => t.id === activeTab).icon, { className: 'w-8 h-8 text-zinc-500' })}
        </div>
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        <p className="text-zinc-500 mt-1">{description}</p>
      </div>
    </div>
  );

  // =============================================================================
  // RENDER: CONTENT ROUTER
  // =============================================================================
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'orders': return renderOrders();
      case 'addresses': return renderAddresses();
      // Payment / Wishlist / Collection are gone entirely -- button AND panel. A hidden tab
      // whose content still renders is not hidden, it is merely harder to find.
      // No flag check here, and that is deliberate. Unlike the gamification DASHBOARD -- which
      // had a genuine second door in App.jsx's ?panel=rewards deep link, and needed guarding at
      // the render site as well as the menu -- activeTab is local state with exactly one setter
      // path: the click handler over the already-filtered tabs array. Nothing else in the file
      // sets it to 'rewards'. A guard here would be unreachable code, and unreachable code that
      // cannot be tested is worse than none: it implies a threat model that does not exist.
      case 'rewards': return renderPlaceholder('Rewards', 'Earn and redeem rewards');
      case 'returns': return renderPlaceholder('Returns', 'View and manage return requests');
      case 'email': return (
        <>
          <EmailPreferencesPage user={user} />
          <EmailHistorySection activeTab={activeTab} />
        </>
      );
      case 'avatar': return <AvatarManager user={user} />;
    }
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================
  return (
    <div className="fixed inset-0 z-50 bg-zinc-950 text-white overflow-hidden" style={{ fontFamily: "'Barlow', sans-serif" }}>
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-[70] px-6 py-3 rounded-lg shadow-xl ${
          notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        } text-white font-semibold`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50">
        {/* Top Bar */}
        <div className="px-4 h-12 sm:h-14 flex items-center justify-between border-b border-zinc-800/50">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-zinc-800 border border-orange-500/30 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/10">
              <span className="font-comic text-base sm:text-lg text-orange-500">M</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-maestro-mono font-bold text-base sm:text-lg" style={{ letterSpacing: '0.15em' }}><span className="text-maestro-gold">M</span><span className="text-white">AESTRO</span></span>
              <span className="text-zinc-600">/</span>
              <span className="text-zinc-400 text-sm">My Account</span>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <button className="relative p-2 hover:bg-zinc-800 rounded-lg transition-colors">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </button>
            <button className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
              <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </button>
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-500 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm cursor-pointer hover:ring-2 hover:ring-orange-500/50 transition-all ml-1">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <nav className="flex items-center overflow-x-auto hide-scrollbar px-2 sm:px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedOrder(null); }}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-zinc-400 hover:text-white hover:border-zinc-600'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {tab.badge && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400'
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      {/* Main Content */}
      <main className="h-[calc(100vh-7rem)] overflow-auto">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          {/* IDENTITY:P3.B — IdentityPreview for authenticated users */}
          {identity?.alias && (
            <div className="mb-6">
              <IdentityPreview identity={identity} />
            </div>
          )}
          {renderContent()}
        </div>
      </main>

      {/* Refund Modal */}
      {refundItem && (
        <RefundRequestModal
          item={refundItem.item}
          order={refundItem.order}
          eligibility={orderEligibility.forItem(refundItem.item.id)}
          onClose={() => setRefundItem(null)}
          onSuccess={() => {
            setRefundItem(null);
            showNotification('Refund request submitted successfully!');
            fetchOrders();
          }}
        />
      )}

      {/* Custom scrollbar hiding CSS */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
